import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { LoadableHelper, LoadableStatus, Logger } from '@aiwyn/shared';
import { ClientRequestGroupType } from '@aiwyn/api';
import { RequestIdentityType } from '../utility';
import { TypedIdentity } from '../models';
import { BaseCommonClientRequest, BaseCommonClientRequestGroup } from '../classes';
import { combineLatest, Observable } from 'rxjs';
import { map, scan, shareReplay } from 'rxjs/operators';
import { CommonClientRequestsState } from '../state';

@Injectable({
  providedIn: 'root'
})
export class CommonClientRequestFactoryService {

  constructor(
    protected readonly store: Store,
    protected readonly logger: Logger,
  ) {}

  protected allClientRequestGroups$: Observable<BaseCommonClientRequestGroup[]> =
    this.store.select(CommonClientRequestsState.getAllClientRequestGroupsLoadables).pipe(
      map(loadables => LoadableHelper.getIdentities(loadables).filter(Boolean) as TypedIdentity<ClientRequestGroupType>[]),
      scan(
        (existingGroups, groupIdentities) => {
          // find and create new groups
          const newGroups = groupIdentities.filter(groupId => !existingGroups.some(group => group.identity === groupId));
          const newGroupModels = newGroups.map(groupId => new BaseCommonClientRequestGroup(groupId, this.store, this.logger));

          // find and remove old groups
          const removedGroups = existingGroups.filter(group => !groupIdentities.some(groupId => group.identity === groupId));
          const filteredExistingGroups = existingGroups.filter(group => !removedGroups.includes(group));

          // return the resulting list of groups
          return filteredExistingGroups.concat(newGroupModels);
        },
        [] as BaseCommonClientRequestGroup[],
      ),
      shareReplay(1),
    );

  getClientRequestGroups$(groupIdentities$: Observable<TypedIdentity<ClientRequestGroupType>>): Observable<BaseCommonClientRequestGroup>;
  getClientRequestGroups$(groupIdentities$: Observable<TypedIdentity<ClientRequestGroupType>[]>): Observable<BaseCommonClientRequestGroup[]>;
  getClientRequestGroups$(
    groupIdentities$: Observable<TypedIdentity<ClientRequestGroupType> | TypedIdentity<ClientRequestGroupType>[]>,
  ): Observable<BaseCommonClientRequestGroup | BaseCommonClientRequestGroup[]> {
    return combineLatest([
      this.allClientRequestGroups$,
      groupIdentities$,
    ]).pipe(
      map(([groups, identities]) => Array.isArray(identities)
        ? groups.filter(group => identities.includes(group.identity))
        : groups.find(group => group.identity === identities),
      ),
    );
  }

  getAllClientRequestGroups$(): Observable<BaseCommonClientRequestGroup[]> {
    return this.allClientRequestGroups$;
  }

  areIdentifiedClientRequestGroupsLoading$(): Observable<boolean> {
    return this.store.select(CommonClientRequestsState.getAllClientRequestGroupsLoadables).pipe(
      map(loadables => loadables.some(loadable => loadable.identity && loadable.status === LoadableStatus.LOADING))
    );
  }

  areUnidentifiedClientRequestGroupsLoading$(): Observable<boolean> {
    return this.store.select(CommonClientRequestsState.getAllClientRequestGroupsLoadables).pipe(
      map(loadables => loadables.some(loadable => !loadable.identity && loadable.status === LoadableStatus.LOADING))
    );
  }

  getClientRequest(requestId: TypedIdentity<RequestIdentityType>): BaseCommonClientRequest {
    return new BaseCommonClientRequest(requestId, this.store, this.logger);
  }
}
